import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Filter, ChevronDown, Sparkles, Crown, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProductCard } from '@/components/products/ProductCard';
import { products as productsApi } from '@/lib/api';
import { PageSeo } from '@/components/seo/PageSeo';

const BijuteriPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('newest');
  const [subcategory, setSubcategory] = useState(searchParams.get('subcategory') || 'all');
  
  const subcategories = [
    { value: 'all', label: 'Tümü', labelEn: 'All' },
    { value: 'Bileklik', label: 'Bileklik', labelEn: 'Bracelets' },
    { value: 'Kolye', label: 'Kolye', labelEn: 'Necklaces' },
    { value: 'Küpe', label: 'Küpe', labelEn: 'Earrings' },
    { value: 'Yüzük', label: 'Yüzük', labelEn: 'Rings' },
    { value: 'Hal Hal', label: 'Halhal', labelEn: 'Anklets' },
    { value: 'Broş', label: 'Broş', labelEn: 'Brooches' },
    { value: 'Saç Aksesuarı', label: 'Saç Aksesuarı', labelEn: 'Hair Accessories' },
  ];
  
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
    setLoading(true);
    try {
      const params = {
        jewelry: true,
        limit: 24,
        page,
        lang: lang || 'tr',
      };
      if (subcategory && subcategory !== 'all') params.jewelry_type = subcategory;
      if (sortBy === 'price-low') {
        params.sort = 'price';
        params.order = 'asc';
      } else if (sortBy === 'price-high') {
        params.sort = 'price';
        params.order = 'desc';
      } else {
        params.sort = 'created_at';
        params.order = 'desc';
      }

      const { data } = await productsApi.list(params);
      if (cancelled) return;
      const rows = data?.products || [];
      setProducts((prev) => (page === 1 ? rows : [...prev, ...rows.filter((row) => !prev.some((item) => item.id === row.id))]));
      setTotal(data?.total || 0);
    } catch (error) {
      console.error('Error fetching bijuteri products:', error);
      if (!cancelled && page === 1) {
        setProducts([]);
        setTotal(0);
      }
    } finally {
      if (!cancelled) setLoading(false);
    }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, sortBy, subcategory, lang]);
  
  const handleSubcategoryChange = (value) => {
    setSubcategory(value);
    setPage(1);
    setProducts([]);
    if (value && value !== 'all') {
      setSearchParams({ subcategory: value });
    } else {
      setSearchParams({});
    }
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setPage(1);
    setProducts([]);
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-pink-50 via-white to-purple-50">
      <PageSeo title={t('bijuteri.title')} description={t('bijuteri.description')} segments={['bijuteri']} />
      <section className="bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4 pt-8 pb-4 md:pt-12 md:pb-6">
          <div className="flex items-center justify-between gap-8">
            <div className="min-w-0 text-center md:text-left">
              <div className="mb-3 flex items-center justify-center gap-2 md:justify-start">
                <Sparkles className="h-5 w-5 text-yellow-300" />
                <span className="text-sm font-medium text-yellow-200">{t('bijuteri.newCollection')}</span>
              </div>
              <h1 className="mb-3 text-3xl font-bold leading-tight sm:text-5xl">
                {t('bijuteri.title')}
              </h1>
              <p className="mb-5 max-w-xl text-base text-white/90 sm:text-lg">
                {t('bijuteri.description')}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2 md:justify-start">
                <Badge className="bg-yellow-400 px-3 py-1.5 text-sm text-yellow-950">
                  <Crown className="mr-1 h-4 w-4" />
                  {total.toLocaleString()}+ {t('common.products')}
                </Badge>
                <Badge className="bg-white/20 px-3 py-1.5 text-sm text-white">
                  <Gift className="mr-1 h-4 w-4" />
                  {t('features.freeShipping')}
                </Badge>
              </div>
            </div>
            <div className="hidden shrink-0 items-end gap-3 lg:flex" aria-hidden="true">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white/15 text-5xl">💎</div>
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-white/15 text-3xl">✨</div>
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/15 text-2xl">💍</div>
            </div>
          </div>
        </div>
        <svg className="block h-12 w-full md:h-16" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0 40C240 80 480 0 720 20C960 40 1200 80 1440 30V80H0V40Z" fill="#fdf2f8" />
        </svg>
      </section>
      
      {/* Filters and Products */}
      <div className="container mx-auto px-4 py-8">
        {/* Filter Bar */}
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between mb-8 bg-white rounded-xl shadow-sm p-4">
          <div className="flex w-full sm:w-auto">
            <Select value={subcategory} onValueChange={handleSubcategoryChange}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="subcategory-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder={lang === 'tr' ? 'Kategori' : 'Category'} />
              </SelectTrigger>
              <SelectContent>
                {subcategories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {lang === 'tr' ? cat.label : cat.labelEn}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <span className="text-sm text-gray-500">
              {total.toLocaleString()} {lang === 'tr' ? 'ürün bulundu' : 'products found'}
            </span>
            <Select value={sortBy} onValueChange={handleSortChange}>
              <SelectTrigger className="w-full sm:w-[220px]" data-testid="sort-filter">
                <SelectValue placeholder={lang === 'tr' ? 'Sıralama' : 'Sort by'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{lang === 'tr' ? 'En Yeni' : 'Newest'}</SelectItem>
                <SelectItem value="price-low">{lang === 'tr' ? 'Fiyat (Düşük-Yüksek)' : 'Price (Low-High)'}</SelectItem>
                <SelectItem value="price-high">{lang === 'tr' ? 'Fiyat (Yüksek-Düşük)' : 'Price (High-Low)'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Products Grid */}
        {loading && products.length === 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 aspect-square rounded-xl mb-3"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <Sparkles className="w-16 h-16 text-pink-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {t('common.noResults')}
            </h3>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} compact />
              ))}
            </div>

            {products.length < total && (
              <div className="text-center mt-8">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setPage((current) => current + 1)}
                  disabled={loading}
                  className="border-pink-300 text-pink-600 hover:bg-pink-50"
                  data-testid="load-more-btn"
                >
                  {t('home.loadMore')}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BijuteriPage;
